/**
 * CAR-IA icon set (v3) — the SVG glyphs used across marketplace components,
 * extracted from the prototypes. Inherit `currentColor`; size via the `size`
 * prop (default 16) or a wrapping element. Stroke icons use stroke-width 2.
 */
import React from 'react';

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
}

const stroke = (children: React.ReactNode) =>
  function StrokeIcon({ size = 16, ...props }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
      >
        {children}
      </svg>
    );
  };

const filled = (children: React.ReactNode) =>
  function FilledIcon({ size = 16, ...props }: IconProps) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
        {children}
      </svg>
    );
  };

export const MenuIcon = stroke(<path d="M3 12h18M3 6h18M3 18h18" />);
export const BellIcon = stroke(
  <>
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </>,
);
export const HeartIcon = filled(
  <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />,
);
export const ShareIcon = stroke(
  <>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
  </>,
);
export const CameraIcon = stroke(
  <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </>,
);
export const ClockIcon = stroke(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </>,
);
export const RoadIcon = stroke(<path d="M3 12h18M3 6h18M3 18h18" />);
export const FuelIcon = stroke(<path d="M3 14h6l4-10 2 16 4-10h2" />);
export const PinIcon = stroke(
  <>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </>,
);
export const StarIcon = filled(<path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z" />);
export const CheckIcon = stroke(<path d="M5 12l5 5L20 7" />);
export const ShieldCheckIcon = stroke(
  <path d="M9 12l2 2 4-4M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
);
export const ShieldIcon = stroke(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />);
export const FileCheckIcon = stroke(
  <>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M9 14l2 2 4-4" />
  </>,
);
export const ChevronRightIcon = stroke(<path d="M9 18l6-6-6-6" />);
export const ChevronDownIcon = stroke(<path d="M6 9l6 6 6-6" />);
export const ArrowDownIcon = stroke(<path d="M12 5v14M5 12l7 7 7-7" />);
export const MessageIcon = stroke(
  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />,
);
export const PhoneIcon = stroke(
  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />,
);
export const SparkleIcon = filled(<path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z" />);
export const SearchIcon = stroke(
  <>
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </>,
);
export const SlidersIcon = stroke(
  <>
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
    <path d="M1 14h6M9 8h6M17 16h6" />
  </>,
);
export const HomeIcon = stroke(
  <>
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <path d="M9 22V12h6v10" />
  </>,
);
export const TagIcon = stroke(
  <>
    <path d="M20.59 13.41L13.42 20.58a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <path d="M7 7h.01" />
  </>,
);

/* --------------------------------------------------------------------------
   Prototype glyphs (docs/html-prototypes/CAR-IA) — paths copied verbatim from
   the <symbol> defs in car-ia-home.html so the rendered shapes are identical.
   These read best at stroke-width 1.7–1.8; pass `strokeWidth` to override the
   default of 2 (props spread after it).
   -------------------------------------------------------------------------- */

/** #i-heart — outline heart used by the "Guardados" top-bar action. */
export const HeartOutlineIcon = stroke(
  <path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />,
);
/** #i-coin — the "Vender" top-bar action. */
export const CoinIcon = stroke(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.8 9a2 2 0 0 0 -1.8 -1h-2a2 2 0 1 0 0 4h2a2 2 0 1 1 0 4h-2a2 2 0 0 1 -1.8 -1" />
    <path d="M12 6v2m0 8v2" />
  </>,
);
/** #i-user — the "Ingresar" top-bar action. */
export const UserIcon = stroke(
  <>
    <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
    <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
  </>,
);

/* Vehicle-type glyphs — "Descubre por tipo" cards + category chips. */
export const SuvIcon = stroke(
  <>
    <path d="M5 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M15 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M5 17h-2v-6l2 -5h9l4 5h3a2 2 0 0 1 2 2v4h-2m-4 0h-6" />
    <path d="M3 11h15" />
  </>,
);
export const PickupIcon = stroke(
  <>
    <path d="M5 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M15 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M5 17h-2v-4h10v4m4 0h2v-6l-3 -4h-3v10" />
    <path d="M3 13h11" />
  </>,
);
export const SedanIcon = stroke(
  <>
    <path d="M5 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M15 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M5 17h-2v-5l2 -4h9l4 4h3a1 1 0 0 1 1 1v4h-2m-4 0h-6" />
    <path d="M3 12h17" />
  </>,
);
export const HatchIcon = stroke(
  <>
    <path d="M5 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M15 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M5 17h-2v-5l3 -5h8l5 5h2a1 1 0 0 1 1 1v4h-2m-4 0h-6" />
    <path d="M3 12h18" />
  </>,
);
export const VanIcon = stroke(
  <>
    <path d="M5 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M15 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M5 17h-2v-9a1 1 0 0 1 1 -1h11l5 6v4h-2m-4 0h-6" />
    <path d="M14 7v5h6" />
  </>,
);

/* Category / value glyphs. */
export const BoltIcon = stroke(<path d="M13 3l-9 12h7l-1 6l9 -12h-7l1 -6z" />);
export const LeafIcon = stroke(
  <>
    <path d="M5 21c.5 -4.5 2.5 -8 7 -10" />
    <path d="M9 18c6.2 0 10 -3.5 10 -10c-.6 -.2 -1.5 -.3 -2.5 -.3c-5.5 0 -9.5 3.3 -9.5 8.3c0 .7 .1 1.3 .3 2z" />
  </>,
);
export const HistoryIcon = stroke(
  <>
    <path d="M12 8v4l2 2" />
    <path d="M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5" />
  </>,
);
export const PiggyIcon = stroke(
  <>
    <path d="M15 11v.01" />
    <path d="M5.7 13h-1.7a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1h1.5" />
    <path d="M14 6a9 9 0 0 1 6.7 3h.3a1 1 0 0 1 1 1v3.5a1 1 0 0 1 -1 1h-1.2a9 9 0 0 1 -2.8 3v2.5a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-.5h-3v.5a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2.5a8 8 0 0 1 -3 -6.5a8 8 0 0 1 8 -8h3z" />
  </>,
);
export const ScaleIcon = stroke(
  <>
    <path d="M7 20l10 0" />
    <path d="M6 6l6 -1l6 1" />
    <path d="M12 3l0 17" />
    <path d="M9 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
    <path d="M21 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
  </>,
);
export const LockIcon = stroke(
  <>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <circle cx="12" cy="16" r="1" />
    <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
  </>,
);
export const CardIcon = stroke(
  <>
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <path d="M3 10h18" />
    <path d="M7 15h.01" />
    <path d="M11 15h2" />
  </>,
);
/** #i-shield — trust glyph with the inner check (distinct from ShieldIcon). */
export const ShieldTrustIcon = stroke(
  <>
    <path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
    <path d="M9 12l2 2l4 -4" />
  </>,
);

/** Empty-state counterpart to CheckIcon — an unfilled ring. */
export const CircleIcon = stroke(<circle cx="12" cy="12" r="8" />);
